<template>
  <v-form>
    <v-card>
      <v-card-title>
        <h4><v-icon>mdi-folder</v-icon>Eintrag bearbeiten</h4>
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="activeTab" >
          <v-tabs-slider />
          <v-tab>
            Details
          </v-tab>
          <v-tab>
            Fahrzeug/Geräte
          </v-tab>
          <v-tab>
            Optionen
          </v-tab>

          <v-tab-item>
            <v-row>
              <v-col pa-2 sm6>
                <label>Start:</label>
                <date-time
                  placeholder="Start"
                  :initialValue="start"
                  @changed="v => start = v"
                  :error="hasStartErrors"
                  :errorMessages="startErrors"
                />
              </v-col>
              <v-col pa-1 sm5>
                <label>Ende:</label>
                <date-time
                  placeholder="Ende"
                  :initialValue="end"
                  @changed="v => end = v"
                  :error="hasEndErrors"
                  :errorMessages="endErrors"
                />
              </v-col>
            </v-row>
            <v-divider />
            <v-row>
              <v-col pa-2 sm6>
                <v-select
                  :items="userOptions"
                  v-model="user"
                  item-value="email"
                  item-text="name"
                  placeholder="Benutzer auswählen"
                  hint="Benutzer auswählen"
                  :error-messages="userErrors"
                  :disabled="userLocked"
                />
                <div v-if="aufgabenparkContextNumber">
                  <strong>Aufgabenpark:</strong> #{{aufgabenparkContextNumber}} {{customName}}
                  <v-btn icon @click="deleteAufgabenparkLink">
                    <v-icon color="blue">mdi-close</v-icon>
                  </v-btn>
                </div>
                <v-autocomplete
                  v-if="!aufgabenparkContextNumber"
                  v-model="aufgabenparkTask"
                  :items="aufgabenparkItems"
                  :loading="isAufgabenparkLoading"
                  :search-input.sync="aufgabenparkSearch"
                  hide-no-data
                  hide-selected
                  item-text="label"
                  item-value="id"
                  label="Aufgabe Suchen"
                  placeholder="Aufgabe Suchen"
                  prepend-icon="mdi-database-search"
                  return-object
                />

                <v-radio-group v-model="taskType" row>
                  <v-radio label="Normales Projekt" value="normal"></v-radio>
                  <v-radio label="Timepark Projekt" value="timepark"></v-radio>
                </v-radio-group>

                <project-select
                  v-if="isTimeparkProject"
                  v-model="project"
                  hide-inactive
                />
              </v-col>
              <v-col pa-2 sm6 v-if="!!comments">
                <h3>Kommentar</h3>
                <br />
                <div>{{ comments }}</div>
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item>
            <machine-usage-select
              :selectedMachine="selectedMachine"
              :selectedTools="selectedTools"
              @selectionChanged="onMachineSelectionChange"
              hide-inactive
            />
          </v-tab-item>
          <v-tab-item>
            <Metadata
              v-for="m in metadata"
              :key="m.name"
              :name="m.name"
              v-model="m.value"
              :metadataDefinition="m.metadataDefinition"
            />
          </v-tab-item>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <v-btn color="default" text @click="doCancel">Abbrechen</v-btn>
        <v-btn color="error" class="white--text" @click="confirmDeleteDialog = true" v-if="!hideDelete">
          <v-icon left dark>mdi-close</v-icon>
          Löschen
        </v-btn>
        <v-btn color="primary" @click="doConfirm" :disabled="!valid" :loading="loading">Speichern</v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="confirmSaveDialog" persistent max-width="500">
      <v-card :color="confirmSaveProminentWarning ? 'error' : undefined">
        <v-card-title :class="{'white--text': confirmSaveProminentWarning}">Zeiten speichern</v-card-title>
        <v-card-text :class="{'white--text': confirmSaveProminentWarning}">
          <div>Sie sind dabei einen Zeit Eintrag über <span class="font-weight-bold">{{projectDurationText}}</span> zu erstellen.</div>
          <div>Wollen Sie den Eintrag dennoch speichern?</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="confirmSaveDialog = false" :color="confirmSaveProminentWarning ? 'white' : 'black'">Abbrechen</v-btn>
          <v-btn text @click="confirmSaving" :color="confirmSaveProminentWarning ? 'white' : 'black'">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmDeleteDialog" persistent max-width="290">
      <v-card>
        <v-card-text>Wirklich löschen?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteDialog = false">Nein</v-btn>
          <v-btn color="primary" text @click="doDelete">Ja</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>

import moment from 'moment';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

import MachineUsageSelect from './MachineUsageSelect';
import ProjectSelect from './ProjectSelect';

import Metadata from '../metadata/Metadata';
import DateTime from '../common/DateTime';

import timeparkApi from '../../timeparkApi';
import _ from "lodash";

export default {
  mixins: [validationMixin],
  validations: {
    start: {
      required,
      isBeforeEnd(value) {
        if (!this.end) {
          return true;
        }

        return moment(value).isBefore(moment(this.end));
      },
    },
    end: {
      required,
    },
    user: {
      required,
    },
    project: {
      isValidTimeparkProject(value) {
        if (!this.isTimeparkProject) {
          return true;
        }

        return !!value && !!value.product && !!value.order && !!value.activity;
      }
    },
    selectedMachine: {
      hasAtLeastOneMachine(value) {
        if (this.selectedTools.length > 0) {
          return !!value;
        }

        return true;
      },
    }
  },
  name: 'EditTimeparkProject',
  props: {
    hideDelete: {
      type: Boolean,
    },
    initialData: {
      type: Object,
    },
    userLock: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    MachineUsageSelect,
    ProjectSelect,
    Metadata,
    DateTime,
  },
  watch: {
    aufgabenparkTask(val) {
      if (val) {
        this.aufgabenparkContextId = val.aufgabenparkContextId;
        this.aufgabenparkContextIsActivity = val.aufgabenparkContextIsActivity;
        this.aufgabenparkContextNumber = val.aufgabenparkContextNumber;
        this.customName = val.aufgabenparkContextName;
      }
    },
    aufgabenparkSearch(val) {
      this.onAufgabenparkSearchChange(val);
    },
    metadata: {
      handler() {
        this.computeMetadataValidity();
      },
      deep: true,
    },
  },
  created() {
    this.onAufgabenparkSearchChange = _.debounce((value) => this.searchAufgabenpark(value), 500);
  },
  computed: {
    aufgabenparkItems() {
      return this.aufgabenparkSearchItems.map(x => ({
        id: x.AufgabenparkContextId,
        label: `#${x.aufgabenparkContextNumber} ${x.aufgabenparkContextName}`,
        ...x
      }));
    },
    userLocked() {
      return this.userLock;
    },
    valid() {
      return !this.$v.$invalid && this.metadataValid;
    },
    hasStartErrors() {
      return this.startErrors.length > 0;
    },
    startErrors() {
      const errors = [];
      if (!this.$v.start.required) {
        errors.push('"Start" ist ein Mussfeld');
      }
      if (!this.$v.start.isBeforeEnd) {
        errors.push('Die Startzeit muss vor der Endzeit liegen');
      }
      return errors;
    },
    hasEndErrors() {
      return this.endErrors.length > 0;
    },
    endErrors() {
      const errors = [];
      if (!this.$v.end.required) {
        errors.push('"End" ist ein Mussfeld');
      }
      return errors;
    },
    userErrors() {
      const errors = [];
      if (!this.$v.user.required) {
        errors.push('"Benutzer" ist ein Mussfeld');
      }
      return errors;
    },
    userOptions() {
      return this.$store.state.users;
    },
    taskType: {
      get() {
        return this.isTimeparkProject ? 'timepark' : 'normal';
      },
      set(value) {
        this.isTimeparkProject = value === 'timepark';
        this.$v.project.$touch();
      },
    },
    projectDuration() {
      if(this.start && this.end) {
        return moment.duration(moment(this.end).diff(moment(this.start)));

      }
      return moment.duration(0);
    },
    projectDurationText() {
      return this.projectDuration.humanize();
    },
    confirmSaveProminentWarning() {
      return this.projectDuration.asMonths() >= 1
    },
  },
  methods: {
    isMetadataValid(metadata) {
      return metadata.length <= 0 ||
        metadata.every(m => !m.metadataDefinition.mandatory || m.value);
    },
    computeMetadataValidity() {
      this.metadataValid = this.isMetadataValid(this.metadata);
    },
    doDelete() {
      this.confirmDeleteDialog = false;
      this.$emit('delete');
    },
    doConfirm() {
      if(this.projectDuration.asDays() > 1){
        this.confirmSaveDialog = true;
      } else {
        this.confirmSaving();
      }
    },
    confirmSaving() {
      this.loading = true;

      const selectedUser = this.userOptions.find(x => x.email === this.user);

      const timeparkProject = {
        start: this.start,
        end: this.end,
        user: selectedUser.name,
        email: selectedUser.email,
        activity: this.isTimeparkProject ? this.project.activity : null,
        order: this.isTimeparkProject ? this.project.order : null,
        product: this.isTimeparkProject ? this.project.product : null,
        machine: this.selectedMachine,
        tools: this.selectedTools,
        metadata: this.metadata,
        aufgabenparkContextId: this.aufgabenparkContextId,
        aufgabenparkContextIsActivity: this.aufgabenparkContextIsActivity,
        aufgabenparkContextNumber: this.aufgabenparkContextNumber,
        customName: this.customName,
      };

      this.$emit('confirm', timeparkProject);
    },
    doCancel() {
      this.$emit('cancel');
    },
    createProjectFromData(data) {
      if (!data.product) {
        return null;
      }

      return {
        product: data.product,
        order: data.order,
        activity: data.activity,
      };
    },
    onProjectChange(project) {
      this.project = project;
      this.$v.project.$touch();
    },
    onMachineSelectionChange(selection) {
      this.selectedMachine = selection.machine;
      this.selectedTools = selection.tools;

      this.$v.selectedMachine.$touch();
    },
    deleteAufgabenparkLink() {
      this.aufgabenparkContextId = null;
      this.aufgabenparkContextIsActivity = false;
      this.aufgabenparkContextNumber = null;
      this.customName = null;
    },
    async searchAufgabenpark(term) {
      this.isAufgabenparkLoading = true;
      try {
        const apiResult = await timeparkApi.searchAufgabenpark(term);
        this.aufgabenparkSearchItems = apiResult.data;
        window.console.log(this.aufgabenparkSearchItems);
      } catch (err) {
        window.console.error(err);
      }
      this.isAufgabenparkLoading = false;
    },
    onAufgabenparkSearchChange() {},
  },
  data() {
    return {
      activeTab: 0,
      confirmDeleteDialog: false,
      confirmSaveDialog: false,
      start: this.initialData.start,
      end: this.initialData.end,
      user: this.initialData.email,
      isTimeparkProject: !!this.initialData.product,
      project: this.createProjectFromData(this.initialData),
      selectedMachine: this.initialData.machine,
      selectedTools: this.initialData.tools,
      metadata: this.initialData.metadata,
      metadataValid: this.isMetadataValid(this.initialData.metadata),
      loading: false,
      comments: this.initialData.comments,
      aufgabenparkContextId: this.initialData.aufgabenparkContextId,
      aufgabenparkContextIsActivity: this.initialData.aufgabenparkContextIsActivity,
      aufgabenparkContextNumber: this.initialData.aufgabenparkContextNumber,
      customName: this.initialData.customName,
      aufgabenparkTask: null,
      isAufgabenparkLoading: false,
      aufgabenparkSearchItems: [],
      aufgabenparkSearch: null,
    };
  },
};
</script>
